'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect } from 'react';

/**
 * Close a popover/popup when clicking outside of its container.
 * Only attaches the listener when `isOpen` is true.
 */
export function useClickOutside(reference, isOpen, onClose, options) {
  const $ = _c(12);
  let t0;
  if ($[0] !== isOpen || $[1] !== onClose || $[2] !== options || $[3] !== reference) {
    t0 = () => {
      if (!isOpen) {
        return;
      }
      const handlePointerDown = pointerEvent => {
        if (options?.guard?.()) {
          return;
        }
        const target = pointerEvent.target;
        const ignoreSelectors = options?.ignoreSelectors ?? [];
        const eventPath = pointerEvent.composedPath();
        if (ignoreSelectors.length > 0 && eventPath.some(pathTarget => pathTarget instanceof Element && ignoreSelectors.some(selector => pathTarget.matches(selector) || pathTarget.closest(selector) != null))) {
          return;
        }
        if (target instanceof Element && !target.isConnected) {
          return;
        }
        const clickedInside = reference.current != null && (eventPath.includes(reference.current) || target instanceof Node && reference.current.contains(target));
        if (clickedInside) {
          return;
        }
        if (options?.additionalRefs) {
          for (const ref of options.additionalRefs) {
            const el = ref != null && "current" in ref ? ref.current : ref;
            if (el != null && (eventPath.includes(el) || target instanceof Node && el.contains(target))) {
              return;
            }
          }
        }
        onClose();
      };
      document.addEventListener("pointerdown", handlePointerDown);
      return () => document.removeEventListener("pointerdown", handlePointerDown);
    };
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = options;
    $[3] = reference;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const t1 = options?.ignoreSelectors;
  const t2 = options?.additionalRefs;
  const t3 = options?.guard;
  let t4;
  if ($[5] !== isOpen || $[6] !== onClose || $[7] !== reference || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = [isOpen, reference, onClose, t1, t2, t3];
    $[5] = isOpen;
    $[6] = onClose;
    $[7] = reference;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t0, t4);
}